import {ComponentType, ReactNode, Suspense, useCallback, useState} from 'react';
import {useIsMobile} from '@legit.health/ui';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import {UserMenuItem} from '@/shared/components/layout/UserMenu';
import AppLoading from '@/shared/components/ui/AppLoading';
import BackToTop from '@/shared/components/ui/BackToTop';
import Header from './Header';
import SkeletonSidebar from './components/SkeletonSidebar';
import noise from './images/dust_scratches.png';

export default function LoggedInLayout({
  children,
  sidebar: UserSidebar,
  extraUserMenuItems,
  tutorial
}: {
  children: ReactNode;
  sidebar: ComponentType<{
    onCloseSidebar: (() => void) | undefined;
  }>;
  extraUserMenuItems?: UserMenuItem[];
  tutorial?: ReactNode;
}) {
  const isMobile = useIsMobile();
  const variant = isMobile ? 'temporary' : 'permanent';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const toggleSidebar = useCallback(function () {
    setIsSidebarCollapsed((value) => !value);
  }, []);

  const backToTopId = 'header';
  return (
    <Suspense fallback={<AppLoading />}>
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh'
        }}
      >
        {tutorial}
        <SwipeableDrawer
          data-testid="userSidebar"
          sx={{
            '& > .MuiPaper-root': {
              position: 'relative',
              width: {
                xs: 200,
                md: 260
              },
              bgcolor: 'secondary.light',
              overflowY: {
                xs: 'auto',
                lg: 'visible'
              }
            }
          }}
          anchor="left"
          open={isSidebarCollapsed}
          onClose={toggleSidebar}
          onOpen={toggleSidebar}
          variant={variant}
        >
          <Suspense fallback={<SkeletonSidebar />}>
            <UserSidebar onCloseSidebar={isMobile ? toggleSidebar : undefined} />
          </Suspense>
        </SwipeableDrawer>

        <Box
          id="mainContent"
          sx={(theme) => ({
            width: {
              xs: '100%',
              lg: 'calc(100% - 260px)'
            },
            mx: 'auto',
            px: {
              xs: theme.layout.containerPaddingHorizontal,
              lg: 6
            },
            pb: {
              xs: 3,
              md: 4
            },
            pt: {
              xs: `calc(${theme.layout.headerHeight.mobile} + ${theme.spacing(3)})`,
              lg: 2
            },
            background: `url(${noise}), ${theme.palette.grey[100]}`
          })}
        >
          <Header
            onClickHamburger={toggleSidebar}
            id={backToTopId}
            extraUserMenuItems={extraUserMenuItems}
          />
          {children}
        </Box>
        <BackToTop anchorId={backToTopId} />
      </Box>
    </Suspense>
  );
}
